import type React from "react";
import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";

type TranslationKeys = keyof typeof translations.en;

interface SubmitRevisionDialogProps {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (note?: string) => Promise<void>;
}

const SubmitRevisionDialog: React.FC<SubmitRevisionDialogProps> = ({
  open,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [revisionNote, setRevisionNote] = useState("");

  useEffect(() => {
    if (!open) {
      setRevisionNote("");
    }
  }, [open]);

  const handleSubmit = async () => {
    const trimmedNote = revisionNote.trim();
    await onSubmit(trimmedNote || undefined);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      data-testid={TestId.ORDER.SUBMIT_REVISION_DIALOG}
    >
      <DialogTitle>{t("orders.submitRevision" as TranslationKeys)}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("orders.submitRevisionDialogHint" as TranslationKeys)}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={4}
          label={t("orders.revisionNote" as TranslationKeys)}
          value={revisionNote}
          onChange={(event) => setRevisionNote(event.target.value)}
          inputProps={{ "data-testid": TestId.ORDER.REVISION_NOTE }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {t("Cancel" as TranslationKeys)}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={loading}
          onClick={handleSubmit}
          data-testid={TestId.ORDER.SUBMIT_REVISION_DIALOG_SUBMIT}
        >
          {t("orders.submitRevision" as TranslationKeys)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitRevisionDialog;
